import { useState } from "react";
import useAiFeatureAvailability from "../../hooks/useAiFeatureAvailability";
import useAiTextImprovement from "../../hooks/useAiTextImprovement";
import AiImproveButton from "./AiImproveButton";
import AiSuggestionReview from "./AiSuggestionReview";
import { AiDocumentType, AiFieldInput, AiTextAction } from "./types";
import "./AiTextImprovement.css";

interface AiActionOption {
  value: AiTextAction;
  label: string;
}

interface AiTextImprovementProps {
  documentType: AiDocumentType;
  fields: AiFieldInput[];
  onApply: (fields: Record<string, string>) => void;
  actions?: AiActionOption[];
  guidance?: string;
  allowFieldSelection?: boolean;
  allowFocus?: boolean;
  deferIndividualApply?: boolean;
}

const defaultActions: AiActionOption[] = [{ value: "Improve", label: "Improve wording" }];
const sessionPlanActions: AiActionOption[] = [
  { value: "Improve", label: "Improve wording" },
  { value: "Shorten", label: "Make more concise" },
  { value: "Structure", label: "Improve structure" },
  { value: "SuggestActivities", label: "Suggest games and activities" },
  { value: "SuggestContent", label: "Suggest content for selected fields" },
  { value: "Professional", label: "Use a professional tone" },
];
const retrospectiveActions: AiActionOption[] = [
  { value: "Improve", label: "Tidy wording" },
  { value: "Shorten", label: "Make more concise" },
  { value: "Professional", label: "Use a professional tone" },
  { value: "ClarifyActions", label: "Clarify learning and actions" },
  { value: "SuggestContent", label: "Help complete selected fields" },
];

export default function AiTextImprovement({
  documentType,
  fields,
  onApply,
  actions,
  guidance,
  allowFieldSelection,
  allowFocus,
  deferIndividualApply = false,
}: AiTextImprovementProps) {
  const enabled = useAiFeatureAvailability();
  const ai = useAiTextImprovement();
  const enhancedControls = documentType === "SessionPlan" || documentType === "Retrospective";
  const availableActions = actions ?? (
    documentType === "SessionPlan"
      ? sessionPlanActions
      : documentType === "Retrospective"
        ? retrospectiveActions
        : defaultActions
  );
  const fieldSelectionEnabled = allowFieldSelection ?? enhancedControls;
  const focusEnabled = allowFocus ?? enhancedControls;
  const [selectedAction, setSelectedAction] = useState<AiTextAction>(availableActions[0].value);
  const [selectedFields, setSelectedFields] = useState<Set<string>>(
    () => new Set(fields.map((field) => field.name)),
  );
  const [focus, setFocus] = useState("");
  const suggestingActivities = selectedAction === "SuggestActivities";
  const suggestingContent = selectedAction === "SuggestContent";
  const generatingSuggestions = suggestingActivities || suggestingContent;
  const summarizing = selectedAction === "Summarize";
  const summarizingRetrospective = documentType === "Retrospective" && summarizing;
  const generatingCompletePlan = documentType === "SessionPlan"
    && suggestingContent
    && selectedFields.size === fields.length;
  const labels = Object.fromEntries(fields.map((field) => [field.name, field.label]));
  const retrospectiveSummaryTargets = new Set(["summary", "keyActionPoints"]);
  const requestedFields = summarizingRetrospective
    ? fields.filter((field) => retrospectiveSummaryTargets.has(field.name))
    : suggestingActivities
    ? fields.filter((field) => field.name === "activities")
    : fields.filter((field) => selectedFields.has(field.name));
  const hasContext = fields.some((field) => Boolean(field.value?.trim()));
  const hasRetrospectiveEvidence = fields.some((field) =>
    !retrospectiveSummaryTargets.has(field.name) && Boolean(field.value?.trim()));
  const hasText = summarizingRetrospective
    ? requestedFields.length === 2 && hasRetrospectiveEvidence
    : suggestingActivities
    ? hasContext || Boolean(focus.trim())
    : suggestingContent
      ? requestedFields.length > 0 && (
        documentType === "SessionPlan"
          ? hasContext || Boolean(focus.trim())
          : hasContext
      )
      : requestedFields.some((field) => Boolean(field.value?.trim()));

  if (!enabled) return null;

  const applyOne = (fieldName: string, value: string) => {
    if (!deferIndividualApply) onApply({ [fieldName]: value });
    ai.setDecision(fieldName, "suggestion");
  };

  const acceptAll = () => {
    onApply(Object.fromEntries(
      ai.suggestions
        .filter((suggestion) => suggestion.decision !== "original")
        .map((suggestion) => [suggestion.fieldName, suggestion.suggestedValue]),
    ));
    ai.cancel();
  };

  const toggleField = (fieldName: string) => {
    setSelectedFields((current) => {
      const next = new Set(current);
      if (next.has(fieldName)) next.delete(fieldName);
      else next.add(fieldName);
      return next;
    });
  };

  const improveButton = (
    <AiImproveButton
      disabled={!hasText}
      loading={ai.loading}
      label={generatingCompletePlan
        ? "Generate session plan"
        : summarizing ? "Summarise with AI"
        : generatingSuggestions ? "Generate suggestions" : undefined}
      loadingLabel={generatingCompletePlan
        ? "Generating session plan..."
        : summarizing ? "Summarising sessions..."
        : generatingSuggestions ? "Generating suggestions..." : undefined}
      onClick={() => void ai.improve(
        documentType,
        selectedAction,
        requestedFields,
        focus,
        generatingSuggestions || summarizingRetrospective ? fields : undefined,
      )}
    />
  );

  return (
    <div className="ai-text-improvement">
      {(availableActions.length > 1 || !generatingCompletePlan) && <div className="ai-text-improvement__controls">
        {availableActions.length > 1 && (
          <label>
            <span>AI action</span>
            <select
              className="form-input"
              value={selectedAction}
              onChange={(event) => setSelectedAction(event.target.value as AiTextAction)}
              disabled={ai.loading}
            >
              {availableActions.map((action) => <option key={action.value} value={action.value}>{action.label}</option>)}
            </select>
          </label>
        )}
        {!generatingCompletePlan && improveButton}
      </div>}

      {(fieldSelectionEnabled || focusEnabled) && (
        <div className="ai-text-improvement__options">
          {fieldSelectionEnabled && (
            <fieldset className="ai-field-selector">
              <legend>{suggestingActivities
                ? "Suggestions will be added to"
                : summarizingRetrospective
                  ? "AI will create"
                : generatingCompletePlan
                  ? "Complete these sections"
                : suggestingContent
                  ? "Generate suggestions for"
                  : "Apply AI to"}</legend>
              <div>
                {fields.map((field) => {
                  const hasFieldText = Boolean(field.value?.trim());
                  const isSuggestionTarget = field.name === "activities";
                  return (
                    <label key={field.name}>
                      <input
                        type="checkbox"
                        checked={summarizingRetrospective
                          ? retrospectiveSummaryTargets.has(field.name)
                          : suggestingActivities ? isSuggestionTarget : selectedFields.has(field.name)}
                        disabled={ai.loading || summarizingRetrospective || suggestingActivities || (!suggestingContent && !hasFieldText)}
                        onChange={() => toggleField(field.name)}
                      />
                      <span>{field.label}</span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          )}
          {focusEnabled && (
            <label className="ai-focus-control">
              <span>{generatingCompletePlan
                ? "Describe the session you want to plan"
                : "What should AI focus on?"} <small>{generatingCompletePlan && !hasContext ? "Required" : "Optional"}</small></span>
              <input
                className="form-input"
                type="text"
                value={focus}
                maxLength={500}
                disabled={ai.loading}
                placeholder={suggestingActivities
                  ? "For example: cooperative games for 8–10 year olds"
                  : suggestingContent
                    ? documentType === "SessionPlan"
                      ? "For example: a 60-minute football session for ages 8–10, focused on confidence"
                      : "For example: identify themes and practical next steps"
                    : "For example: make the timings easier to scan"}
                onChange={(event) => setFocus(event.target.value)}
              />
            </label>
          )}
          {suggestingActivities && (
            <p className="ai-generation-hint">
              AI will suggest named activities with a short setup, instructions and the intended benefit. Review suitability before adding them to the plan.
            </p>
          )}
        </div>
      )}

      {generatingCompletePlan && (
        <div className="ai-text-improvement__generate-action">{improveButton}</div>
      )}

      {ai.error && (
        <div className="ai-text-improvement__error" role="alert">
          <span>{ai.error}</span>
          <button type="button" className="secondary-button" onClick={ai.retry}>Retry</button>
        </div>
      )}

      {ai.suggestions.length > 0 && (
        <AiSuggestionReview
          labels={labels}
          suggestions={ai.suggestions}
          warnings={ai.warnings}
          showCharacterCount={documentType === "Sms"}
          guidance={guidance}
          onChange={ai.updateSuggestion}
          onUseSuggestion={(suggestion) => applyOne(suggestion.fieldName, suggestion.suggestedValue)}
          onKeepOriginal={(fieldName) => ai.setDecision(fieldName, "original")}
          onUseAll={acceptAll}
          onCancel={ai.cancel}
        />
      )}
    </div>
  );
}
